/**
 * Theme Store
 *
 * Manages light/dark theme preference with system detection.
 * Persists user choice to localStorage. Falls back to OS preference.
 *
 * Values: 'light' | 'dark' | 'system'
 */

import { create } from 'zustand';

// Shared with the web app on the same origin, which spells the system choice 'auto'
const STORAGE_KEY = 'theme_preference';
const LEGACY_STORAGE_KEY = 'rrecce-theme';

export function readStoredPreference(storage = localStorage) {
  try {
    const stored = storage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    return 'system';
  }
}

function writeStoredPreference(preference, storage = localStorage) {
  try {
    storage.setItem(STORAGE_KEY, preference === 'system' ? 'auto' : preference);
  } catch {
    // Private browsing or a full quota: the choice lasts for the page only
  }
}

export function migrateLegacyPreference(storage = localStorage) {
  try {
    const legacy = storage.getItem(LEGACY_STORAGE_KEY);
    if (legacy == null) return;
    if (storage.getItem(STORAGE_KEY) == null && (legacy === 'light' || legacy === 'dark')) {
      storage.setItem(STORAGE_KEY, legacy);
    }
    storage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // Private browsing or a full quota: the legacy choice is left where it is
  }
}

/**
 * Resolve the effective theme ('light' or 'dark') from a preference value.
 */
function resolveTheme(preference) {
  if (preference === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }
  return preference;
}

/**
 * Apply the resolved theme to the document.
 */
function applyTheme(theme) {
  document.documentElement.setAttribute('data-theme', theme);
}

export const useThemeStore = create((set, get) => ({
  // 'light' | 'dark' | 'system'
  preference: 'system',
  // Resolved effective theme: 'light' | 'dark'
  theme: 'dark',

  /**
   * Initialise from localStorage + OS preference.
   * Should be called once on app startup.
   */
  init: () => {
    const apply = (preference) => {
      const theme = resolveTheme(preference);
      applyTheme(theme);
      set({ preference, theme });
    };
    migrateLegacyPreference();
    apply(readStoredPreference());

    // Listen for OS preference changes (only matters when preference === 'system')
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    mql.addEventListener('change', () => {
      if (get().preference === 'system') apply('system');
    });

    // A choice made in the web app, or another tab, in the same browser
    window.addEventListener('storage', (event) => {
      if (event.key === STORAGE_KEY || event.key === null) apply(readStoredPreference());
    });
  },

  /**
   * Set the theme preference. Pass 'light', 'dark', or 'system'.
   */
  setPreference: (preference) => {
    const theme = resolveTheme(preference);
    writeStoredPreference(preference);
    applyTheme(theme);
    set({ preference, theme });
  },
}));
